import { useState } from 'react'
import { Link } from 'react-router'
import { Banner, Button, SectionLabel, StressBox } from '../../design/components'
import type { EncounterCard } from '../encounters/types'
import { useCardEditing } from './cardEditing'
import type { EncounterType } from './types'
import styles from './live.module.css'

/**
 * One copy's own stress, when the card on screen belongs to a template.
 *
 * The card is read per **character** — every zombie has the same boxes, because they are the same
 * sheet — so the card's own `isStressed` is the template's and says nothing about which copy is
 * nearly out. This replaces that half: which boxes are ticked, and what to call when one is
 * pressed.
 */
export interface InstanceStress {
  /** Slot ids this copy has taken. The card's own `isStressed` is ignored while this is here. */
  marked: readonly number[]
  busy: boolean
  onToggle: (slotId: number, isStressed: boolean) => void
}

export interface BattleCardProps {
  card: EncounterCard
  /** Where "back to the encounter" goes. */
  sessionId: string
  /**
   * Which lens to read the sheet through.
   *
   * The encounter's own type, and it can change mid-session — a fight that becomes a negotiation
   * is an ordinary evening at a table. Defaults to Combat so a caller that has not been taught
   * about lenses gets what it always got.
   */
  lens?: EncounterType
  /**
   * This copy's stress, for a clone.
   *
   * Absent for everybody else, and absence is what says "the sheet is the truth here" — a player's
   * stress track has to survive the evening, so their card writes to the character exactly as it
   * always has.
   */
  instanceStress?: InstanceStress
}

/**
 * One participant, at a glance, while an encounter is running.
 *
 * **Compactness is the requirement, not a preference.** This sits in the Game Master's right-hand
 * column beside a stage they are also reading, so anything that costs a scroll costs attention at
 * the table. Everything here answers one of three questions — *can they still take part*, *what
 * can they do to someone*, *what can I invoke against them* — and anything that answers none of
 * them is a link to the sheet instead.
 *
 * **The middle question is the one the lens changes.** In a combat encounter, what they can do to
 * someone is attacks and armour. In a social one it is the skills that carry an offence, defence
 * or initiative in Social — the same sheet, read for the exchange actually happening. The server
 * already works out both halves and ships them on the same card
 * (`combat.socialSkills` alongside `attacks`), so switching the encounter's type switches what is
 * drawn without refetching a thing.
 *
 * The two questions either side of it do not change, and that is deliberate rather than an
 * omission: stress and consequences are what a social exchange spends, and an aspect is invoked in
 * a negotiation exactly as it is in a brawl. The one character who has no consequences half is a
 * `Temp`, and that is not the lens — it is what a Temp is.
 *
 * Stress and consequences are also the exception to the compaction: they are shown **in full**,
 * every box and every slot, because they are the numbers that change during an encounter and a
 * collapsed summary of them is a thing you have to open exactly when you are busiest.
 *
 * **Editable only where the caller owns the character.** `canEdit` is the server's word — the same
 * ownership rule the character mutations enforce — so a Game Master ticks stress on their own
 * NPCs, Temps and GM-PCs, and reads a player's PC without being offered a control that would be
 * refused (`docs/web-api/phase-9-kickoff.md` §7).
 *
 * **One thing on this card is not the character's, and it is the stress.** A `Temp` is a template
 * and a session holds as many copies of it as the room needs, so the same card is on screen for
 * Zombie 1 and for Zombie 5 — and ticking a box has to mark one of them. See `instanceStress`.
 */
export function BattleCard({ card, sessionId, lens = 'Combat', instanceStress }: BattleCardProps) {
  const editing = useCardEditing(card.characterId ?? 0, sessionId)
  const canEdit = card.canEdit === true
  const social = lens === 'Social'

  // Null for everybody whose stress is on their sheet, which is everybody who is not a clone.
  const marked = instanceStress === undefined ? null : new Set(instanceStress.marked)
  const stressBusy = instanceStress === undefined ? editing.busy : instanceStress.busy

  return (
    <div className={styles.battleCard}>
      <header className={styles.battleHead}>
        {card.portraitUrl != null && (
          <img className={styles.battlePortrait} src={card.portraitUrl} alt="" />
        )}
        <span className={styles.battleName}>
          {card.name}
          <span className={styles.rowMeta}>
            {card.type}
            {card.isBeastForm === true && ' · beast form'}
          </span>
        </span>
      </header>

      {!canEdit && (
        <p className={styles.readOnly}>
          Read-only — this character belongs to a player. You can see everything and change nothing.
        </p>
      )}

      {editing.failure !== null && (
        <Banner
          tone="error"
          actions={
            <Button variant="secondary" onClick={editing.dismiss}>
              Dismiss
            </Button>
          }
        >
          {editing.failure}
        </Banner>
      )}

      {/* ----- the two things that change during a fight ----- */}

      {/* The boxes are the template's; which of them are ticked may not be.
          A clone's damage is its own — six zombies share a sheet, so a box ticked on the sheet
          would be a box ticked on all six — so for a clone the filled state and the write both
          come from the session instead. Everybody else writes to their character, which is what
          makes their stress survive the evening. */}
      <Section label="Stress">
        {(card.stress?.tracks ?? []).map((track) => (
          <div key={track.type} className={styles.trackRow}>
            <span className={styles.trackName}>{track.type}</span>
            <span className={styles.trackBoxes}>
              {(track.boxes ?? []).map((box) => (
                <StressBox
                  key={box.id}
                  value={box.value ?? 0}
                  filled={marked === null ? box.isStressed === true : marked.has(box.id ?? 0)}
                  trackName={String(track.type)}
                  disabled={!canEdit || stressBusy}
                  {...(canEdit
                    ? {
                        onToggle: (next: boolean) =>
                          instanceStress === undefined
                            ? editing.setStress.mutate({ slotId: box.id ?? 0, isStressed: next })
                            : instanceStress.onToggle(box.id ?? 0, next),
                      }
                    : {})}
                />
              ))}
            </span>
          </div>
        ))}
      </Section>

      {/* Absent, not empty, for a Temp — and the section goes with it.
          A Temp is the Game Master's scratch template, the zombie there are six of, and a mook
          does not absorb a hit into a lasting wound: it is taken out. So the server sends no
          consequences at all rather than an empty list, and drawing "Consequences — None." over
          nothing would say "not yet" where the truth is "never", on the one card that has to stay
          short enough to read mid-exchange. */}
      {card.consequences != null && (
        <Section label="Consequences">
          {(card.consequences.slots ?? []).length === 0 ? (
            <p className={styles.note}>None.</p>
          ) : (
            (card.consequences.slots ?? []).map((slot) => (
              <ConsequenceRow
                key={slot.id}
                slot={slot}
                canEdit={canEdit}
                busy={editing.busy}
                onSet={(name) => editing.setConsequence.mutate({ slotId: slot.id ?? 0, name })}
                onClear={() => editing.clearConsequence.mutate({ slotId: slot.id ?? 0 })}
              />
            ))
          )}
        </Section>
      )}

      {/* ----- what they can do to someone ----- */}

      {social ? <SocialSkills card={card} /> : <Attacks card={card} />}

      {card.hasSpellcasting === true && <Magic card={card} />}

      {/* Armour stops a blow and nothing else. In a social encounter it is a coat. */}
      {!social && (
        <Section label="Armour">
          {(card.combat?.armor ?? []).length === 0 ? (
            <p className={styles.note}>None.</p>
          ) : (
            (card.combat?.armor ?? []).map((piece) => (
              <div key={piece.equipmentId} className={styles.listRow}>
                <span>{piece.name}</span>
                <span className={styles.numeric}>{piece.effectiveArmor}</span>
              </div>
            ))
          )}
        </Section>
      )}

      {/* ----- what can be invoked or compelled ----- */}

      <Section label="Aspects">
        {(card.aspects?.aspects ?? []).length === 0 ? (
          <p className={styles.note}>None written down.</p>
        ) : (
          <ul className={styles.aspectList}>
            {(card.aspects?.aspects ?? []).map((aspect, index) => (
              <li key={`${aspect}-${index}`}>{aspect}</li>
            ))}
          </ul>
        )}
      </Section>

      {/* ----- carried, listed and no more ----- */}

      {(card.equipment?.items ?? []).length > 0 && (
        <Section label="Carried">
          <p className={styles.inlineList}>
            {(card.equipment?.items ?? []).map((item) => item.name).join(' · ')}
          </p>
        </Section>
      )}

      {(card.enchantedItems?.items ?? []).length > 0 && (
        <Section label="Enchanted">
          <p className={styles.inlineList}>
            {(card.enchantedItems?.items ?? []).map((item) => item.name).join(' · ')}
          </p>
        </Section>
      )}

      <div className={styles.cardActions}>
        {/* The escape hatch. Everything this card leaves out is one tap away, and the way back
            is a link rather than the browser's back button so it survives a reload. */}
        <Link to={`/characters/${card.characterId}?from=live&session=${sessionId}`}>
          <Button variant="secondary">Open the sheet</Button>
        </Link>
      </div>
    </div>
  )
}

/**
 * What they can hit someone with, and how hard.
 *
 * One row per *option* rather than per kind: a character with two knives and a pistol has three
 * rows, because "which weapon" is a choice made at the table. Every number is worked out
 * server-side — which skill rolls it, whether Might is added, the beast-form exception — so this
 * prints two columns and nothing is derived here.
 */
function Attacks({ card }: { card: EncounterCard }) {
  const attacks = card.attacks ?? []

  return (
    <Section label="Attacks">
      {attacks.length === 0 ? (
        <p className={styles.note}>Nothing to hit with.</p>
      ) : (
        <table className={styles.attackTable}>
          <thead>
            <tr>
              <th scope="col">Attack</th>
              <th scope="col">Hit</th>
              <th scope="col">Dmg</th>
            </tr>
          </thead>
          <tbody>
            {attacks.map((attack, index) => (
              <tr key={`${attack.name}-${attack.kind}-${index}`}>
                <td>
                  {attack.name}
                  <span className={styles.rowMeta}> {attack.kind}</span>
                </td>
                {/* Just the number. Which skill rolls it stays on the tooltip: a Game Master
                    running a fight already knows a gun uses Guns, and the abbreviation was
                    three characters of noise on every single row. */}
                <td title={attack.toHitSkill ?? undefined}>{signed(attack.toHit ?? 0)}</td>
                <td title={attack.damageBreakdown ?? undefined}>{attack.damage}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  )
}

/**
 * The same question, asked of a social encounter: what can they do to someone.
 *
 * **The skills, not the attacks.** These are the ones whose offence, defence or initiative is
 * Social — the server picks them, using the same rule the Blazor card has always used, and ships
 * them on every card whatever the encounter's type. So the switch from a fight to a negotiation
 * costs no request: the numbers were already here.
 *
 * Both the rung and its ladder value are printed. The name is what a Game Master says out loud
 * ("Rapport at Good"); the number is what gets added to the roll, and reading it off the name is
 * a lookup nobody should do mid-exchange.
 */
function SocialSkills({ card }: { card: EncounterCard }) {
  const skills = [...(card.combat?.socialSkills ?? [])].sort(
    (a, b) => (b.levelValue ?? 0) - (a.levelValue ?? 0),
  )

  return (
    <Section label="Social">
      {skills.length === 0 ? (
        <p className={styles.note}>
          Nothing social trained. They can still be talked at; they are just not good at it.
        </p>
      ) : (
        <table className={styles.attackTable}>
          <thead>
            <tr>
              <th scope="col">Skill</th>
              <th scope="col">Rung</th>
              <th scope="col">Mod</th>
            </tr>
          </thead>
          <tbody>
            {skills.map((skill) => (
              <tr key={skill.skillId}>
                <td>{skill.name}</td>
                <td>
                  <span className={styles.rowMeta}>{skill.level}</span>
                </td>
                <td>{signed(skill.levelValue ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  )
}

/**
 * The magic block.
 *
 * Power and Control are Conviction and Discipline, and the per-element rows say where a bonus
 * came from — a specialization, a focus item, or both. The server already attributes them
 * (`powerSource` / `controlSource`), so the card prints the attribution rather than re-deriving it.
 */
function Magic({ card }: { card: EncounterCard }) {
  const modifiers = card.combat?.evocationModifiers ?? []
  const rotes = card.roteSpells?.spells ?? []

  return (
    <Section label="Magic">
      {/* Power is Conviction and Control is Discipline. A spellcaster's Game Master knows that,
          and printing the derivation on every card was a lesson nobody needed twice. */}
      <div className={styles.magicHead}>
        <span>
          Power <strong className={styles.numeric}>{card.combat?.convictionLevel ?? 0}</strong>
        </span>
        <span>
          Control <strong className={styles.numeric}>{card.combat?.disciplineLevel ?? 0}</strong>
        </span>
      </div>

      {modifiers.length > 0 && (
        <table className={styles.attackTable}>
          <thead>
            <tr>
              <th scope="col">Element</th>
              <th scope="col">Pwr</th>
              <th scope="col">Ctl</th>
            </tr>
          </thead>
          <tbody>
            {modifiers.map((modifier) => (
              <tr key={modifier.element}>
                <td>{modifier.element}</td>
                <td title={modifier.powerSource ?? undefined}>{signed(modifier.power ?? 0)}</td>
                <td title={modifier.controlSource ?? undefined}>{signed(modifier.control ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {rotes.length > 0 && (
        <>
          <SectionLabel tone="muted" micro>
            Rotes
          </SectionLabel>
          {rotes.map((rote) => (
            <div key={rote.id} className={styles.listRow}>
              <span>{rote.name}</span>
              {/* Shifts is the power a rote goes off at; stress is what it costs to cast. Both
                  matter at the table and neither is derivable from the other. */}
              <span className={styles.numeric}>
                {rote.shifts}
                <span className={styles.rowMeta}> shifts / {rote.stress} stress</span>
              </span>
            </div>
          ))}
        </>
      )}

      {(card.focusItems?.items ?? []).length > 0 && (
        <p className={styles.inlineList}>
          <span className={styles.rowMeta}>Foci: </span>
          {(card.focusItems?.items ?? []).map((item) => item.name).join(' · ')}
        </p>
      )}
    </Section>
  )
}

/**
 * One consequence slot: what kind it is, how bad, and what took it.
 *
 * **The type is shown** — Physical, Mental, Social or Any — because which one a slot is decides
 * whether it can absorb the hit currently being rolled, and that is the question being asked when
 * a Game Master looks at this list mid-exchange.
 *
 * Taking one is a one-line form rather than an inline edit, because the API has no update: a slot
 * is filled or cleared, and a second fill is a `409`. That is the character sheet's rule and not a
 * new one — and it is the right shape anyway. A consequence is *taken*; rewording it afterwards is
 * rare enough to be worth two taps.
 */
function ConsequenceRow({
  slot,
  canEdit,
  busy,
  onSet,
  onClear,
}: {
  slot: NonNullable<NonNullable<EncounterCard['consequences']>['slots']>[number]
  canEdit: boolean
  busy: boolean
  onSet: (name: string) => void
  onClear: () => void
}) {
  const [taking, setTaking] = useState(false)
  const [name, setName] = useState('')

  const occupied = slot.isOccupied === true

  return (
    <div className={`${styles.consequenceRow} ${occupied ? styles.consequenceTaken : ''}`}>
      <span className={styles.consequenceLevel}>
        <span className={styles.consequenceType}>{shortType(slot.type)}</span>
        {slot.level}
        <span className={styles.rowMeta}> −{slot.penalty}</span>
      </span>

      {taking && canEdit ? (
        <form
          className={styles.consequenceForm}
          onSubmit={(event) => {
            event.preventDefault()
            if (name.trim() === '') return

            onSet(name.trim())
            setName('')
            setTaking(false)
          }}
        >
          <input
            className={styles.consequenceInput}
            value={name}
            autoFocus
            maxLength={120}
            placeholder="Cracked ribs"
            aria-label={`Consequence for the ${slot.level} slot`}
            onChange={(event) => setName(event.target.value)}
          />
          <button type="submit" className={styles.miniButton} disabled={busy || name.trim() === ''}>
            Take
          </button>
          <button type="button" className={styles.miniButton} onClick={() => setTaking(false)}>
            Cancel
          </button>
        </form>
      ) : (
        <>
          <span className={styles.consequenceText}>
            {slot.consequence?.name ?? <span className={styles.rowMeta}>empty</span>}
          </span>

          {canEdit && (
            <button
              type="button"
              className={styles.miniButton}
              disabled={busy}
              onClick={() => (occupied ? onClear() : setTaking(true))}
            >
              {occupied ? 'Clear' : 'Take'}
            </button>
          )}
        </>
      )}
    </div>
  )
}

/** Four characters, enough to tell the four kinds apart without widening the column. */
function shortType(type: string | undefined): string {
  switch (type) {
    case 'Physical':
      return 'Phys'
    case 'Mental':
      return 'Ment'
    case 'Social':
      return 'Soc'
    default:
      return 'Any'
  }
}

/** A labelled block. Flat markup rather than nested cards — a card inside a card wastes 16px twice. */
function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className={styles.cardSection}>
      <SectionLabel tone="muted" micro>
        {label}
      </SectionLabel>
      {children}
    </section>
  )
}

function signed(value: number): string {
  return value > 0 ? `+${value}` : String(value)
}
